import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { marketDataService } from '../../services/marketDataService';
import { TimeRange, StockPrice, CompanyDetails } from '../../types/market';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine, ReferenceArea } from 'recharts';
import LoadingSpinner from '../common/LoadingSpinner';
import { Alert } from '../ui/alert';
import { SaveItemButton } from '../common/SaveItemButton';
import StockSparkline from '../../pages/dashboard/StockSparkline';
import { useNavigate } from 'react-router-dom'; // Add this import
import { usePrompt } from '../../contexts/PromptContext'; // Add this import
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Bookmark,
  DollarSign, 
  BarChart2, 
  Users,
  Building2, 
  Globe,
  ExternalLink,
  RefreshCw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  Clock,
  Star,
  MessageSquarePlus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useWatchlists } from '../../hooks/useWatchlists';
import { BookmarkPlus } from 'lucide-react';

// Time range options for chart - Updated to more standard options
const timeRanges: { label: string; value: TimeRange; days: number }[] = [
  { label: '1D', value: '1d', days: 1 },
  { label: '5D', value: '5d', days: 5 },
  { label: '1M', value: '1mo', days: 30 },
  { label: '3M', value: '3mo', days: 90 },
  { label: '6M', value: '6mo', days: 180 },
  { label: '1Y', value: '1y', days: 365 },
  { label: '5Y', value: '5y', days: 1825 },
  { label: 'MAX', value: 'max', days: 9999 },
];

interface NewsItem {
  title: string;
  description?: string;
  url: string;
  source: string;
  favicon?: string;
  date: string;
  timestamp: string;
}

// Enhanced Constants with clear ET designation and support for holidays
const MARKET_SESSIONS = {
  PRE_MARKET_START: 4, // 4:00 AM ET
  MARKET_OPEN: 9.5, // 9:30 AM ET
  MARKET_CLOSE: 16, // 4:00 PM ET
  AFTER_HOURS_END: 20, // 8:00 PM ET
  HALF_DAY_CLOSE: 13, // 1:00 PM ET (early close time for half days)
  
  // 2025 US Market Holidays
  HOLIDAYS: [
    "2025-01-01", // New Year's Day
    "2025-01-20", // Martin Luther King, Jr. Day
    "2025-02-17", // Presidents' Day
    "2025-04-18", // Good Friday
    "2025-05-26", // Memorial Day
    "2025-06-19", // Juneteenth
    "2025-07-04", // Independence Day
    "2025-09-01", // Labor Day
    "2025-11-27", // Thanksgiving Day
    "2025-12-25", // Christmas Day
  ],
  
  // 2025 US Market Half Days (market closes at 1:00 PM ET)
  HALF_DAYS: [
    "2025-11-28", // Day after Thanksgiving
    "2025-12-24", // Christmas Eve
  ]
};

// Map exchange codes to human-readable names
const EXCHANGE_MAP: Record<string, string> = {
  'XNAS': 'NASDAQ',
  'XNYS': 'NYSE',
  'XASE': 'AMEX',
  'ARCX': 'NYSE Arca',
  'XBOS': 'Boston',
  'XCIS': 'NSE',
  'XCHI': 'Chicago',
  'XPHL': 'Philadelphia',
  'BATS': 'BATS',
  'XCBO': 'CBOE',
  'IEXG': 'IEX',
};

// Helper function to get readable exchange name
const getReadableExchange = (exchangeCode: string): string => {
  return EXCHANGE_MAP[exchangeCode] || exchangeCode;
};

// Enhanced function to check if a date is a market holiday
const isMarketHoliday = (date: Date): boolean => {
  const dateStr = date.toISOString().split('T')[0];
  return MARKET_SESSIONS.HOLIDAYS.includes(dateStr);
};

// Enhanced function to check if a date is a half trading day
const isHalfDay = (date: Date): boolean => {
  const dateStr = date.toISOString().split('T')[0];
  return MARKET_SESSIONS.HALF_DAYS.includes(dateStr);
};

// Helper function to convert ET times to local times for display
const convertETtoLocal = (etHours: number): string => {
  const now = new Date();
  const etDate = new Date(now);
  
  // Set the date to ET time
  etDate.setHours(Math.floor(etHours));
  etDate.setMinutes((etHours % 1) * 60);
  etDate.setSeconds(0);
  
  // Format the date in the local timezone
  return etDate.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit',
    timeZoneName: 'short' 
  });
};

// Enhanced getMarketSessionInfo to account for holidays and half-days
const getMarketSessionInfo = (timestamp: number) => {
  const etDate = new Date(timestamp);
  const etOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
  };
  const etTimeString = etDate.toLocaleString('en-US', etOptions);
  const [etTimeOnly, period] = etTimeString.split(' ');
  const [etHourStr, etMinStr] = etTimeOnly.split(':');
  let etHour = parseInt(etHourStr);
  const etMin = parseInt(etMinStr);
  
  // Convert to 24-hour format if needed
  if (period === 'PM' && etHour !== 12) etHour += 12;
  if (period === 'AM' && etHour === 12) etHour = 0;
  
  const etTotalHours = etHour + (etMin / 60);
  const day = etDate.getDay();
  
  // Check for holidays
  if (isMarketHoliday(etDate)) {
    return { inSession: false, sessionType: 'holiday', label: 'Market Holiday' };
  }
  
  // Weekend
  if (day === 0 || day === 6) {
    return { inSession: false, sessionType: 'weekend', label: 'Weekend' };
  }
  
  // Check for half days (market closes at 1:00 PM ET)
  const marketCloseTime = isHalfDay(etDate) ? 13 : MARKET_SESSIONS.MARKET_CLOSE;
  
  if (etTotalHours < MARKET_SESSIONS.PRE_MARKET_START) {
    return { inSession: false, sessionType: 'closed', label: 'Market Closed' };
  } else if (etTotalHours < MARKET_SESSIONS.MARKET_OPEN) {
    return { inSession: true, sessionType: 'pre', label: 'Pre-Market' };
  } else if (etTotalHours < marketCloseTime) {
    return { inSession: true, sessionType: 'regular', label: 'Regular Hours' };
  } else if (etTotalHours < MARKET_SESSIONS.AFTER_HOURS_END) {
    return { inSession: true, sessionType: 'after', label: 'After Hours' };
  } else {
    return { inSession: false, sessionType: 'closed', label: 'Market Closed' };
  }
};

// Helper function to determine the next market open time
const getNextMarketOpen = (currentDate: Date): string => {
  // Clone the date to avoid modifying the original
  const nextDate = new Date(currentDate);
  
  // Move to the next day
  nextDate.setDate(nextDate.getDate() + 1);
  
  // Get the day of week for the next day
  const dayOfWeek = nextDate.getDay();
  
  // If it's a weekend, skip to Monday
  if (dayOfWeek === 0) { // Sunday
    nextDate.setDate(nextDate.getDate() + 1); // Move to Monday
  } else if (dayOfWeek === 6) { // Saturday
    nextDate.setDate(nextDate.getDate() + 2); // Move to Monday
  }
  
  // Check if the next day is a holiday
  const nextDateOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  };
  const nextDateString = nextDate.toLocaleDateString('en-US', nextDateOptions);
  const [nextMonth, nextDay, nextYear] = nextDateString.split('/');
  const nextDateFormatted = `${nextYear}-${nextMonth.padStart(2, '0')}-${nextDay.padStart(2, '0')}`;
  
  // If the next day is a holiday, recursively call to find the next trading day
  if (MARKET_SESSIONS.HOLIDAYS.includes(nextDateFormatted)) {
    return getNextMarketOpen(nextDate);
  }
  
  // Format the next opening day and time
  const nextDayName = nextDate.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' });
  return `${nextDayName} ${Math.floor(MARKET_SESSIONS.MARKET_OPEN)}:${(MARKET_SESSIONS.MARKET_OPEN % 1) * 60} AM ET`;
};

interface StockViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticker: string;
  onAnalyze?: (prompt: string) => void;
  fullScreenMode?: boolean;
  fromHomePage?: boolean; // New prop to know if we're on the home page
}

export const StockViewModal: React.FC<StockViewModalProps> = ({
  isOpen,
  onClose,
  ticker,
  onAnalyze,
  fullScreenMode = false,
  fromHomePage = false // Default to false if not provided
}) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const { isItemSaved } = useWatchlists();
  const navigate = useNavigate(); // Add navigate hook
  const { setSelectedStockChip } = usePrompt(); // Add usePrompt hook
  const [stockDetails, setStockDetails] = useState<CompanyDetails | null>(null);
  const [stockData, setStockData] = useState<StockPrice[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('1d');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  
  const [showNews, setShowNews] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [dayChange, setDayChange] = useState<{ change: number; changePercent: number }>({ change: 0, changePercent: 0 });

  // Calculate and set the day change separately to keep it consistent between timeframes
  const calculateAndSetDayChange = (data: StockPrice[]) => {
    if (data.length === 0) {
      setDayChange({ change: 0, changePercent: 0 });
      return;
    }

    const latest = data[data.length - 1].c;
    
    // For day change, we always look for the same-day open or previous close
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Try to find today's opening price first
    const todayData = data.find(point => {
      const pointDate = new Date(point.t);
      pointDate.setHours(0, 0, 0, 0);
      return pointDate.getTime() === today.getTime();
    });
    
    // Reference price for day change calculation
    let dayReference;
    
    if (todayData) {
      // Use today's opening price if available
      dayReference = todayData.o;
    } else if (data[0].prevClose) {
      // If no today data but we have previous close, use it
      dayReference = data[0].prevClose;
    } else {
      // Fall back to using the first point in the data
      dayReference = data[0].o;
    }
    
    const dayChangeValue = latest - dayReference;
    const dayChangePercent = (dayChangeValue / dayReference) * 100;
    
    // Only update the dayChange state
    setDayChange({ 
      change: dayChangeValue, 
      changePercent: dayChangePercent 
    });
  };

  // Calculate day change - separate function that runs only once when data first loads
  const calculateDayChange = (data: StockPrice[]): { change: number; changePercent: number } => {
    if (data.length === 0) return { change: 0, changePercent: 0 };
    
    const latest = data[data.length - 1].c;
    
    // For day change calculation, always look for same-day open or previous close
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Try to find today's opening price first
    const todayData = data.find(point => {
      const pointDate = new Date(point.t);
      pointDate.setHours(0, 0, 0, 0);
      return pointDate.getTime() === today.getTime();
    });
    
    // Reference price for day change calculation
    let dayReference;
    
    if (todayData) {
      // Use today's opening price if available
      dayReference = todayData.o;
    } else if (data[0].prevClose) {
      // If no today data but we have previous close, use it
      dayReference = data[0].prevClose;
    } else {
      // Fall back to using the first point in the data
      dayReference = data[0].o;
    }
    
    const dayChangeValue = latest - dayReference;
    const dayChangePercent = (dayChangeValue / dayReference) * 100;
    
    return { 
      change: dayChangeValue, 
      changePercent: dayChangePercent 
    };
  };

  // Format large numbers (like market cap) in a readable format
  const formatLargeNumber = (num: number): string => {
    if (!num) return '$0';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  // Enhanced time formatter for better display on the chart
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  
  if (selectedTimeRange === '1d') {
    // For intraday view, show time with AM/PM in a cleaner format
    return date.toLocaleTimeString([], { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } 
  
  if (selectedTimeRange === '5d') {
    // For 5-day view, include both day and time for better context
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateDay = new Date(date);
    dateDay.setHours(0, 0, 0, 0);
    
    const diffDays = Math.floor((today.getTime() - dateDay.getTime()) / (1000 * 60 * 60 * 24));
    
    // Use more specific formatting to prevent duplicate labels
    if (diffDays === 0) {
      return `Today ${date.toLocaleTimeString([], { hour: 'numeric', hour12: true })}`;
    } else if (diffDays === 1) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: 'numeric', hour12: true })}`;
    } else {
      // Show weekday with specific time
      return date.toLocaleDateString([], {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  } 
  
  if (selectedTimeRange === '1mo') {
    // For 1-month view, show day and abbreviated month
    return date.toLocaleDateString([], {
      day: 'numeric',
      month: 'short'
    });
  }
  
  if (selectedTimeRange === '3mo' || selectedTimeRange === '6mo') {
    // For 3-6 month views, show abbreviated month and day
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  }
  
  if (selectedTimeRange === '1y') {
    // For 1-year view, show month and year
    return date.toLocaleDateString([], {
      month: 'short',
      year: 'numeric'
    });
  }
  
  // For longer timeframes (5Y, MAX), show month and year with appropriate spacing
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric'
  });
  
  // For 5Y and MAX, show less frequent labels to avoid overcrowding
  // Only show Jan for years to reduce clutter
  const month = date.getMonth();
  if (month === 0 || selectedTimeRange !== 'max') {
    return formatter.format(date);
  } else {
    return ''; // Return empty string for non-January months in MAX view
  }
}

  // Enhanced time range change handler
  const handleTimeRangeChange = (range: TimeRange) => {
    setSelectedTimeRange(range);
    setIsLoading(true);
    
    const fetchData = async () => {
      try {
        const isAuthenticated = !!user;
        
        const stockDataPromise = isAuthenticated
          ? marketDataService.getStockData(ticker, range)
          : marketDataService.getPublicStockData(ticker, range);
          
        const data = await stockDataPromise;
        
        // Ensure all data points have required fields
        const validData = data.filter(point => 
          point.t && point.o && point.h && point.l && point.c && point.v
        );
        
        // Add additional data points if sparse for better visualization
        const enhancedData = ensureDataGranularity(validData, range);
        
        // We only update the stock data for the timeframe, but DON'T update the day change
        // This keeps day change consistent regardless of timeframe selection
        setStockData(enhancedData);
      } catch (err) {
        console.error('Failed to fetch data for timeframe change:', err);
        setError('Failed to update chart data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  };

  // Calculate price change and percentage
  const calculateChange = () => {
    if (stockData.length === 0) return { change: 0, changePercent: 0 };
    
    const current = stockData[stockData.length - 1].c;
    let reference;
  
    if (selectedTimeRange === '1d') {
      // For 1d view, compare to the opening price
      reference = stockData[0].o;
    } else {
      // For other timeframes, compare to the first close in the data
      reference = stockData[0].c;
    }
    
    const change = current - reference;
    const changePercent = (change / reference) * 100;
    
    return { change, changePercent };
  };

  // Calculate price change for both daily and selected period
  const calculateChanges = (data: StockPrice[]) => {
    if (data.length === 0) return { 
      periodChange: { change: 0, changePercent: 0 },
      dailyChange: { change: 0, changePercent: 0 }
    };
    
    const latest = data[data.length - 1].c;
    
    // For period change
    const periodStart = data[0].c;
    const periodChange = latest - periodStart;
    const periodChangePercent = (periodChange / periodStart) * 100;
    
    // For daily change (always calculate regardless of timeframe)
    let dayReference;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find the opening price for today or the previous close
    const todayData = data.find(point => {
      const pointDate = new Date(point.t);
      pointDate.setHours(0, 0, 0, 0);
      return pointDate.getTime() === today.getTime() && point.o;
    });
    
    if (todayData) {
      dayReference = todayData.o; // Today's opening price
    } else {
      // Use previous close if available
      const prevClose = data[0].prevClose || data[0].o;
      dayReference = prevClose;
    }
    
    const dayChangeValue = latest - dayReference;
    const dayChangePercent = (dayChangeValue / dayReference) * 100;
    
    return {
      periodChange: { change: periodChange, changePercent: periodChangePercent },
      dailyChange: { change: dayChangeValue, changePercent: dayChangePercent }
    };
  };

  // Determine market session for current price
const getCurrentMarketSession = () => {
  const now = new Date();
  
  // Get ET date and time information
  const etOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  };
  
  const etDateTimeString = now.toLocaleString('en-US', etOptions);
  const [etDateStr, etTimeStr] = etDateTimeString.split(', ');
  const [etMonth, etDay, etYear] = etDateStr.split('/');
  const [etHours, etMinutes] = etTimeStr.split(':').map(Number);
  
  const etDateFormatted = `${etYear}-${etMonth.padStart(2, '0')}-${etDay.padStart(2, '0')}`;
  const etTimeInMinutes = etHours * 60 + etMinutes;
  
  // Check for weekend
  const day = now.getDay();
  if (day === 0 || day === 6) {
    return { 
      status: 'closed', 
      label: 'Weekend',
      nextOpen: getNextMarketOpen(now)
    };
  }
  
  // Check if today is a holiday
  if (MARKET_SESSIONS.HOLIDAYS.includes(etDateFormatted)) {
    return { 
      status: 'closed', 
      label: 'Market Holiday',
      nextOpen: getNextMarketOpen(now)
    };
  }
  
  // Check if today is a half-day
  const isHalfDay = MARKET_SESSIONS.HALF_DAYS.includes(etDateFormatted);
  const marketCloseHour = isHalfDay ? MARKET_SESSIONS.HALF_DAY_CLOSE : MARKET_SESSIONS.MARKET_CLOSE;
  const marketCloseMinutes = marketCloseHour * 60;
  
  // Determine session type
  if (etTimeInMinutes < MARKET_SESSIONS.PRE_MARKET_START * 60) {
    return { 
      status: 'closed', 
      label: 'After Hours (Previous Day)',
      nextOpen: `${MARKET_SESSIONS.PRE_MARKET_START}:00 AM ET`
    };
  } else if (etTimeInMinutes < MARKET_SESSIONS.MARKET_OPEN * 60) {
    return { 
      status: 'pre', 
      label: 'Pre-Market',
      openTime: `${Math.floor(MARKET_SESSIONS.MARKET_OPEN)}:${Math.round((MARKET_SESSIONS.MARKET_OPEN % 1) * 60).toString().padStart(2, '0')} AM ET`
    };
  } else if (etTimeInMinutes < marketCloseMinutes) {
    return { 
      status: 'regular', 
      label: isHalfDay ? 'Regular Hours (Early Close)' : 'Regular Hours',
      closeTime: `${marketCloseHour}:00 ${marketCloseHour >= 12 ? 'PM' : 'AM'} ET`
    };
  } else if (etTimeInMinutes < MARKET_SESSIONS.AFTER_HOURS_END * 60) {
    return { 
      status: 'after', 
      label: 'After Hours',
      endTime: `${MARKET_SESSIONS.AFTER_HOURS_END}:00 PM ET`
    };
  } else {
    return { 
      status: 'closed', 
      label: 'Market Closed',
      nextOpen: getNextMarketOpen(now)
    };
  }
};

  // Handle refresh click
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await marketDataService.getPublicStockData(ticker, selectedTimeRange);
      setStockData(data);
    } catch (err) {
      console.error('Failed to refresh data:', err);
    } finally {
      setRefreshing(false);
    }
  };
  
  // Handle analyze button click - enhanced to work in different contexts
  const handleAnalyzeClick = () => {
    if (!stockDetails) return;

    // Create the stock chip data
    const stockChipData = {
      ticker,
      companyName: stockDetails.name,
      prefix: '' // You may want to add a default research mode prefix here
    };

    if (onAnalyze) {
      // If onAnalyze is provided, use that (likely for chat-in-progress)
      onAnalyze(`Analyze ${ticker} (${stockDetails.name}): provide recent performance, key metrics, and outlook.`);
      onClose();
    } else if (fromHomePage) {
      // If we're on the home page, set the stock chip in the input area and close
      setSelectedStockChip(stockChipData);
      onClose();
    } else {
      // Otherwise, navigate to the dashboard/market-chat with the stock chip data
      setSelectedStockChip(stockChipData);
      navigate('/dashboard/market-chat');
      onClose();
    }
  };

  // Create a new function specifically for adding to chat
  const handleAddToChat = () => {
    if (!stockDetails) return;

    // Create the stock chip data
    const stockChipData = {
      ticker,
      companyName: stockDetails.name,
      prefix: '' // Default prefix
    };

    // If we're on the home page, set the stock chip directly
    if (fromHomePage) {
      setSelectedStockChip(stockChipData);
      onClose();
    } else {
      // Otherwise, navigate to the chat with the stock chip
      setSelectedStockChip(stockChipData);
      navigate('/dashboard/market-chat');
      onClose();
    }
  };

  // Fetch stock data when ticker or time range changes
  useEffect(() => {
  if (!isOpen || !ticker) return;

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use either authenticated or public endpoints based on auth status
      const isAuthenticated = !!user;
      
      const stockDataPromise = isAuthenticated
        ? marketDataService.getStockData(ticker, selectedTimeRange)
        : marketDataService.getPublicStockData(ticker, selectedTimeRange);
      
      const detailsPromise = isAuthenticated
        ? marketDataService.getStockDetails(ticker)
        : marketDataService.getPublicCompanyDetails(ticker);
      
      // Add news fetch
      const newsPromise = marketDataService.getStockNews(ticker, 5);
      
      // Await all promises
      const [data, details, newsData] = await Promise.all([stockDataPromise, detailsPromise, newsPromise]);
      
      // Filter out invalid data points and ensure we have proper timestamps
      const validData = data.filter(point => 
        point.t && point.o && point.h && point.l && point.c && point.v
      );
      
      // If using 1d timeframe with too few points, add artificial points for better visualization
      if (selectedTimeRange === '1d' && validData.length < 5) {
        console.warn('Not enough data points for 1d view, using extended range');
        // Try fetching 5d data and filtering just the last day
        const extendedData = await (isAuthenticated
          ? marketDataService.getStockData(ticker, '5d')
          : marketDataService.getPublicStockData(ticker, '5d'));
          
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = today.getTime();
        
        const filteredData = extendedData.filter(point => 
          point.t >= todayTimestamp
        );
        
        if (filteredData.length > 0) {
          setStockData(filteredData);
          // Calculate the day change when first loading data
          if (dayChange.change === 0 && dayChange.changePercent === 0) {
            const changes = calculateDayChange(filteredData);
            setDayChange(changes);
          }
        } else {
          setStockData(validData);
          // Calculate the day change when first loading data
          if (dayChange.change === 0 && dayChange.changePercent === 0) {
            const changes = calculateDayChange(validData);
            setDayChange(changes);
          }
        }
      } else {
        setStockData(validData);
        // Calculate the day change when first loading data
        if (dayChange.change === 0 && dayChange.changePercent === 0) {
          const changes = calculateDayChange(validData);
          setDayChange(changes);
        }
      }
      
      setStockDetails(details);
        
        // Fetch news - mock data for now
        setNews(newsData);
      } catch (err) {
        console.error('Failed to fetch stock data:', err);
        setError('Failed to load stock data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, ticker, user, selectedTimeRange]);

  // Calculate change values for the selected timeframe (this will change with timeframe)
  const { change, changePercent } = calculateChange();
  const isPositive = change >= 0;
  
  // Store initial day change to keep it consistent
  useEffect(() => {
    if (stockData.length > 0) {
      // Calculate day change only once when data is first loaded
      const latest = stockData[stockData.length - 1].c;
      
      // For day change, we always look for the same-day open or previous close
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Try to find today's opening price first
      const todayData = stockData.find(point => {
        const pointDate = new Date(point.t);
        pointDate.setHours(0, 0, 0, 0);
        return pointDate.getTime() === today.getTime();
      });
      
      // Reference price for day change calculation
      let dayReference;
      
      if (todayData) {
        // Use today's opening price if available
        dayReference = todayData.o;
      } else if (stockData[0].prevClose) {
        // If no today data but we have previous close, use it
        dayReference = stockData[0].prevClose;
      } else {
        // Fall back to using the first point in the data
        dayReference = stockData[0].o;
      }
      
      const dayChangeValue = latest - dayReference;
      const dayChangePercent = (dayChangeValue / dayReference) * 100;
      
      // Set the day change just once
      setDayChange({ 
        change: dayChangeValue, 
        changePercent: dayChangePercent 
      });
    }
  }, [stockData]); // Only run when stockData changes, not using a condition as dependency
  
  // Better chart data formatter
  const processChartData = () => {
    // First optimize the data display based on time of day for intraday charts
    const optimizedData = optimizeChartDisplay(stockData, selectedTimeRange);
    
    // Then map to the chart format
    return optimizedData.map(dataPoint => {
      // Ensure timestamp is a number
      const timestamp = typeof dataPoint.t === 'number' 
        ? dataPoint.t 
        : new Date(dataPoint.t).getTime();
        
      return {
        timestamp,
        price: dataPoint.c,
        open: dataPoint.o,
        high: dataPoint.h,
        low: dataPoint.l,
        volume: dataPoint.v,
        // Add previous close if available
        prevClose: dataPoint.prevClose
      };
    });
  };

  const chartData = processChartData();

  const modalClasses = fullScreenMode 
    ? "fixed inset-0 z-50 flex items-center justify-center"
    : "fixed inset-0 z-50 flex items-center justify-center p-4";

  const contentClasses = fullScreenMode
    ? `flex flex-col w-full h-full overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`
    : `relative flex flex-col w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden border ${
        isDarkMode 
        ? 'bg-gray-900/90 border-gray-700/40 backdrop-blur-xl' 
        : 'bg-white/90 border-gray-200/40 backdrop-blur-xl'
      }`;

  // Early return if modal should not be visible
  if (!isOpen || !ticker) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={modalClasses}
      >
        {/* Backdrop with blur */}
        <motion.div 
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        />
        
        {/* Modal Content */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          className={contentClasses}
        >
          {/* Enhanced background gradients with subtle effects */}
          <div className={`absolute inset-0 bg-gradient-to-br ${
            isPositive 
            ? 'from-green-500/5 via-emerald-500/3 to-transparent' 
            : 'from-red-500/5 via-rose-500/3 to-transparent'
          } opacity-60 pointer-events-none`} />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-500/5 via-transparent to-transparent pointer-events-none blur-3xl" />
          
          {/* Redesigned modern header with better spacing and visual hierarchy */}
          <div className={`relative flex items-start justify-between p-6 ${isDarkMode ? 'border-gray-800/50' : 'border-gray-200/50'}`}>
            <div className="flex items-start space-x-4">
              {/* Company logo with enhanced container */}
              <div className={`relative overflow-hidden w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-lg ${
                isPositive 
                ? 'from-green-500/10 to-emerald-500/5 border border-green-500/20' 
                : 'from-red-500/10 to-rose-500/5 border border-red-500/20'
              }`}>
                <div className="absolute inset-0 bg-gradient-to-br opacity-50 mix-blend-overlay" />
                <img 
                  src={`/api/market/company-logo/${ticker}`}
                  alt={stockDetails?.name || ticker}
                  className="w-14 h-14 object-contain transition-all duration-200 hover:scale-105"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = `flex items-center justify-center w-full h-full ${isDarkMode ? 'text-white/90' : 'text-gray-800'} font-bold text-2xl`;
                      fallback.textContent = ticker.charAt(0).toUpperCase();
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              
              <div className="flex flex-col">
                <div className="flex items-center mb-1">
                  <h2 className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {ticker}
                  </h2>
                  {stockDetails && (
                    <div className="flex items-center space-x-2">
                      <div className={`ml-2.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                      } flex items-center`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isDarkMode ? 'bg-blue-400' : 'bg-blue-500'}`}></span>
                        {getReadableExchange(stockDetails.exchange)}
                      </div>
                      
                      {/* Moved classification info inline with exchange */}
                      {stockDetails?.sector && (
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700'
                        } flex items-center`}>
                          <Building2 className="w-3 h-3 mr-1" />
                          {stockDetails.sector}
                        </div>
                      )}
                      
                      {stockDetails?.industry && (
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-50 text-violet-700'
                        } flex items-center overflow-hidden`}>
                          <Globe className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{stockDetails.industry}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {stockDetails && (
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} pr-6`}>
                    {stockDetails.name}
                  </p>
                )}
              </div>
            </div>
            
            {/* Action buttons with enhanced visual design - restyled to use consistent icon buttons with tooltips */}
            <div className="flex items-center space-x-2">
              {/* Add to Chat button - updated to use handleAddToChat */}
              <div className="relative group">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddToChat}
                  className={`p-2.5 rounded-lg transition-all ${
                    isDarkMode 
                      ? 'bg-gray-800/90 hover:bg-blue-600/90 text-gray-400 hover:text-white' 
                      : 'bg-gray-100/90 hover:bg-blue-500/90 text-gray-500 hover:text-white'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-700 focus:ring-offset-gray-900' : 'focus:ring-blue-400 focus:ring-offset-white'
                  }`}
                  aria-label="Add to chat"
                >
                  <MessageSquarePlus className="w-5 h-5" />
                </motion.button>
                <div className={`absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-200 border border-gray-700' 
                    : 'bg-white text-gray-600 shadow-md border border-gray-100'
                } opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200`}>
                  Add to chat
                </div>
              </div>
              
              {/* Save button - restyled for consistency with tooltip */}
              <div className="relative group">
                <SaveItemButton
                  type="STOCK"
                  identifier={ticker}
                  title={stockDetails?.name || ticker}
                  metadata={{
                    sector: stockDetails?.sector,
                    industry: stockDetails?.industry,
                    addedFrom: 'stockModal'
                  }}
                  variant="icon"
                  className={`p-2.5 rounded-lg transition-all ${
                    isDarkMode 
                      ? 'bg-gray-800/90 hover:bg-indigo-600/90 text-gray-400 hover:text-white' 
                      : 'bg-gray-100/90 hover:bg-indigo-500/90 text-gray-500 hover:text-white'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-indigo-700 focus:ring-offset-gray-900' : 'focus:ring-indigo-400 focus:ring-offset-white'
                  }`}
                />
                <div className={`absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-200 border border-gray-700' 
                    : 'bg-white text-gray-600 shadow-md border border-gray-100'
                } opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200`}>
                  Add to watchlist
                </div>
              </div>
              
              {/* Close button - enhanced with better styling and tooltip */}
              <div className="relative group">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className={`p-2.5 rounded-lg transition-all ${
                    isDarkMode 
                      ? 'bg-gray-800/90 hover:bg-red-600/90 text-gray-400 hover:text-white' 
                      : 'bg-gray-100/90 hover:bg-red-500/90 text-gray-500 hover:text-white'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-red-700 focus:ring-offset-gray-900' : 'focus:ring-red-400 focus:ring-offset-white'
                  }`}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </motion.button>
                <div className={`absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-200 border border-gray-700' 
                    : 'bg-white text-gray-600 shadow-md border border-gray-100'
                } opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200`}>
                  Close
                </div>
              </div>
            </div>
          </div>
          
          {/* Subtle divider line */}
          <div className={`h-px mx-6 ${isDarkMode ? 'bg-gradient-to-r from-gray-800/50 via-gray-700/50 to-gray-800/50' : 'bg-gradient-to-r from-gray-200/50 via-gray-300/50 to-gray-200/50'}`} />
          
          {/* Content */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="flex flex-col items-center">
                <LoadingSpinner size="lg" />
                <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading {ticker} data...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <Alert variant="destructive">
                {error}
              </Alert>
            </div>
          ) : (
            <div className="flex-1 overflow-auto relative">
              {/* Redesigned Price Info Section */}
              <div className={`${isDarkMode ? 'bg-gray-800/40' : 'bg-white/60'} border-b ${isDarkMode ? 'border-gray-700/40' : 'border-gray-200/40'}`}>
                <div className="grid grid-cols-12 gap-4 p-5">
                  {/* Price and Changes - Left Side - Enhanced with Google Finance style layout */}
                  <div className="col-span-8 flex flex-col">
                    <div className="flex items-center space-x-3">
                      {/* Main Price with session indicator */}
                      <div className="flex items-center">
                        <h3 className={`text-3xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          ${stockData.length > 0 && stockData[stockData.length - 1].latestPrice !== undefined
                            ? stockData[stockData.length - 1].latestPrice!.toFixed(2) 
                            : stockData.length > 0 
                              ? stockData[stockData.length - 1].c.toFixed(2) 
                              : '0.00'}
                        </h3>

                        {/* Market Session Indicator - More prominent position */}
                        {stockData.length > 0 && (
                          <div className={`ml-3 px-2.5 py-1 text-xs font-medium rounded-md ${
                            (() => {
                              const session = getCurrentMarketSession();
                              switch (session.status) {
                                case 'pre': return isDarkMode ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'bg-blue-50 text-blue-600 border border-blue-200';
                                case 'regular': return isDarkMode ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-green-50 text-green-600 border border-green-200';
                                case 'after': return isDarkMode ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-purple-50 text-purple-600 border border-purple-200';
                                default: return isDarkMode ? 'bg-gray-700/60 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-600 border border-gray-200';
                              }
                            })()
                          }`}>
                            {getCurrentMarketSession().label}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Price Changes - Improved Google Finance style with clearer session context */}
                    <div className="flex items-center flex-wrap gap-2 mt-2.5">
                      {/* Day Change - Enhanced visibility */}
                      <div className={`flex items-center px-3 py-1.5 rounded-lg ${
                        dayChange.change >= 0 
                          ? isDarkMode ? 'bg-green-500/15 text-green-300' : 'bg-green-50 text-green-700' 
                          : isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-700'
                      }`}>
                        {dayChange.change >= 0 
                          ? <ArrowUp className="w-3.5 h-3.5 mr-1.5" /> 
                          : <ArrowDown className="w-3.5 h-3.5 mr-1.5" />}
                        <span className="text-sm font-semibold">
                          {dayChange.change >= 0 ? '+' : ''}{dayChange.change.toFixed(2)} ({dayChange.changePercent.toFixed(2)}%)
                        </span>
                        <span className="ml-1.5 text-xs font-medium opacity-80">Today</span>
                      </div>
                      
                      {/* Period Change - For non-daily views */}
                      {selectedTimeRange !== '1d' && (
                        <div className={`flex items-center px-3 py-1.5 rounded-lg ${
                          change >= 0 
                            ? isDarkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700' 
                            : isDarkMode ? 'bg-orange-500/15 text-orange-300' : 'bg-orange-50 text-orange-700'
                        }`}>
                          {change >= 0 
                            ? <ArrowUp className="w-3.5 h-3.5 mr-1.5" /> 
                            : <ArrowDown className="w-3.5 h-3.5 mr-1.5" />}
                          <span className="text-sm font-semibold">
                            {change >= 0 ? '+' : ''}{change.toFixed(2)} ({changePercent.toFixed(2)}%)
                          </span>
                          <span className="ml-1.5 text-xs font-medium opacity-80">
                            {timeRanges.find(r => r.value === selectedTimeRange)?.label || selectedTimeRange}
                          </span>
                        </div>
                      )}
                      
                      {/* Previous Close Reference - Added for clarity */}
                      {chartData.length > 0 && chartData[0].prevClose && (
                        <div className={`flex items-center px-3 py-1.5 rounded-lg ${
                          isDarkMode ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          <span className="text-xs font-medium">
                            Prev Close: ${chartData[0].prevClose.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Last Updated - More informative with time zone */}
                    <div className={`mt-2.5 text-xs flex items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Clock className="w-3 h-3 mr-1" />
                      <span>
                        {(() => {
                          const session = getCurrentMarketSession();
                          if (session.status === 'regular') {
                            return 'Live • Updated: ';
                          } else if (session.status === 'pre' || session.status === 'after') {
                            return `${session.label} • Updated: `;
                          } else {
                            return 'Market Closed • Updated: ';
                          }
                        })()}
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  
                  {/* OHLC Values - Right Side with enhanced Google Finance style */}
                  <div className="col-span-4">
                    <div className={`grid grid-cols-2 gap-x-4 gap-y-2 ${isDarkMode ? 'bg-gray-800/80' : 'bg-gray-50/90'} border ${isDarkMode ? 'border-gray-700/60' : 'border-gray-200/60'} rounded-lg p-3 h-full`}>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Open</span>
                        <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${chartData[0]?.open.toFixed(2) || '0.00'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>High</span>
                        <span className={`text-xs font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                          ${Math.max(...chartData.map(d => d.high)).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Low</span>
                        <span className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          ${Math.min(...chartData.map(d => d.low)).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Close</span>
                        <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          ${chartData[chartData.length - 1]?.price.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Bar with Time Range Selector and Chart Controls */}
                <div className="flex items-center justify-between p-3 border-t border-dashed ${isDarkMode ? 'border-gray-700/30' : 'border-gray-200/60'}">
                  {/* Time Range Selector - Google Finance style tabs */}
                  <div className="flex items-center">
                    <div className="flex space-x-1">
                      {timeRanges.map(range => (
                        <button
                          key={range.value}
                          onClick={() => handleTimeRangeChange(range.value)}
                          disabled={isLoading}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            isLoading && selectedTimeRange === range.value ? 'opacity-50' : ''
                          } ${
                            selectedTimeRange === range.value
                              ? isDarkMode 
                                ? 'bg-blue-500/30 text-blue-300 ring-1 ring-blue-500/50' 
                                : 'bg-blue-100 text-blue-700 ring-1 ring-blue-500/30'
                              : isDarkMode 
                                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30' 
                                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          {range.label}
                          {isLoading && selectedTimeRange === range.value && (
                            <span className="ml-1 inline-block animate-spin">⟳</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Right side controls - Enhanced Google Finance style */}
                  <div className="flex items-center space-x-4">
                    {/* Session Legend with improved visual design */}
                    <div className="flex items-center space-x-3 pr-4 border-r ${isDarkMode ? 'border-gray-700/40' : 'border-gray-200/60'}">
                      <div className="flex items-center space-x-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-400 opacity-80"></div>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pre</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400 opacity-80"></div>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Regular</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-purple-400 opacity-80"></div>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>After</span>
                      </div>
                    </div>
                    
                    {/* Display options with dropdown (placeholder for future enhancement) */}
                    <div className="relative group">
                      <button 
                        className={`flex items-center space-x-1 p-1.5 rounded-md ${
                          isDarkMode 
                            ? 'hover:bg-gray-700/50 text-gray-400 hover:text-white' 
                            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <BarChart2 className="w-4 h-4" />
                        <span className="text-xs font-medium">Line</span>
                      </button>
                    </div>
                    
                    {/* Refresh button with improved styling */}
                    <button 
                      onClick={handleRefresh} 
                      disabled={refreshing}
                      className={`p-1.5 rounded-md transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white' 
                          : 'bg-gray-100/80 hover:bg-gray-200/80 text-gray-600 hover:text-gray-800'
                      } ${refreshing ? 'opacity-50' : ''}`}
                      title="Refresh data"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Chart - Enhanced Trading Style (Google Finance inspired) */}
              <div className={`h-[400px] w-full p-4 ${isDarkMode ? 'bg-gray-800/60 backdrop-blur-sm' : 'bg-white/80 backdrop-blur-sm'} relative border-b ${isDarkMode ? 'border-gray-700/40' : 'border-gray-200/40'}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 24 }}>
                    <defs>
                      {/* Enhanced gradient for price area - smoother transition */}
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop 
                          offset="5%" 
                          stopColor={isPositive ? "#10B981" : "#EF4444"} 
                          stopOpacity={0.3}
                        />
                        <stop 
                          offset="95%" 
                          stopColor={isPositive ? "#10B981" : "#EF4444"} 
                          stopOpacity={0}
                        />
                      </linearGradient>

                      {/* Session background patterns - improved visibility */}
                      <pattern id="preMarketPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                        <rect width="6" height="6" fill="#3B82F6" fillOpacity={0.1} />
                        <line x1="0" y1="0" x2="0" y2="6" stroke="#3B82F6" strokeWidth="1" strokeOpacity={0.2} />
                      </pattern>
                      <pattern id="regularPattern" patternUnits="userSpaceOnUse" width="6" height="6">
                        <rect width="6" height="6" fill={isPositive ? "#10B981" : "#EF4444"} fillOpacity={0.05} />
                      </pattern>
                      <pattern id="afterHoursPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(-45)">
                        <rect width="6" height="6" fill="#8B5CF6" fillOpacity={0.1} />
                        <line x1="0" y1="0" x2="0" y2="6" stroke="#8B5CF6" strokeWidth="1" strokeOpacity={0.2} />
                      </pattern>
                      
                      {/* Gradient for price line above/below previous close */}
                      <linearGradient id="priceLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={isPositive ? "#10B981" : "#EF4444"} />
                        <stop offset="100%" stopColor={isPositive ? "#10B981" : "#EF4444"} />
                      </linearGradient>
                    </defs>

                    {/* Session backgrounds for 1D view - improved Google Finance style */}
                    {selectedTimeRange === '1d' && chartData.length > 0 && (
                      <>
                        {/* Pre-market session background */}
                        <ReferenceArea
                          x1={chartData[0].timestamp}
                          x2={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours, minutes] = etTime.split(':').map(Number);
                            return hours === 9 && minutes >= 30;
                          })?.timestamp || chartData[0].timestamp}
                          strokeOpacity={0}
                          fill="url(#preMarketPattern)"
                        />
                        
                        {/* Regular trading hours background */}
                        <ReferenceArea
                          x1={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours, minutes] = etTime.split(':').map(Number);
                            return hours === 9 && minutes >= 30;
                          })?.timestamp || chartData[0].timestamp}
                          x2={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours] = etTime.split(':').map(Number);
                            return hours >= 16;
                          })?.timestamp || chartData[chartData.length - 1].timestamp}
                          fill="url(#regularPattern)"
                        />
                        
                        {/* After-hours session background */}
                        <ReferenceArea
                          x1={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours] = etTime.split(':').map(Number);
                            return hours >= 16;
                          })?.timestamp || chartData[chartData.length - 1].timestamp}
                          x2={chartData[chartData.length - 1].timestamp}
                          fill="url(#afterHoursPattern)"
                        />
                        
                        {/* Market open transition line (9:30 AM ET) */}
                        <ReferenceLine
                          x={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours, minutes] = etTime.split(':').map(Number);
                            return hours === 9 && minutes >= 30;
                          })?.timestamp}
                          stroke="#10B981"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          label={{ 
                            value: '9:30 AM ET', 
                            position: 'insideBottomRight',
                            fill: isDarkMode ? '#10B981' : '#047857',
                            fontSize: 10,
                            offset: 10,
                          }}
                        />
                        
                        {/* Market close transition line (4:00 PM ET) */}
                        <ReferenceLine
                          x={chartData.find(d => {
                            const date = new Date(d.timestamp);
                            const etOptions: Intl.DateTimeFormatOptions = {
                              timeZone: 'America/New_York',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            };
                            const etTime = date.toLocaleString('en-US', etOptions);
                            const [hours] = etTime.split(':').map(Number);
                            return hours >= 16;
                          })?.timestamp}
                          stroke="#EF4444"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          label={{ 
                            value: '4:00 PM ET', 
                            position: 'insideTopRight',
                            fill: isDarkMode ? '#EF4444' : '#B91C1C',
                            fontSize: 10,
                            offset: -10,
                          }}
                        />
                      </>
                    )}

                    {/* Grid lines - more subtle for professional look */}
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke={isDarkMode ? 'rgba(55, 65, 81, 0.15)' : 'rgba(229, 231, 235, 0.7)'} 
                      horizontal={true}
                      vertical={true}
                    />
                    
                    {/* Previous day close reference line - Enhanced prominence */}
                    {chartData.length > 0 && chartData[0].prevClose && (
                      <ReferenceLine 
                        y={chartData[0].prevClose} 
                        stroke={isDarkMode ? 'rgba(250, 200, 60, 0.7)' : 'rgba(217, 119, 6, 0.7)'} 
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        label={{
                          value: `Prev Close: $${chartData[0].prevClose.toFixed(2)}`,
                          fill: isDarkMode ? 'rgba(250, 200, 60, 0.9)' : 'rgba(217, 119, 6, 0.9)',
                          position: 'insideBottomRight',
                          fontSize: 11
                        }}
                      />
                    )}
                    
                    {/* Enhanced tooltip - Google Finance inspired with more data */}
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        if (name === 'price') return [`$${value.toFixed(2)}`, 'Price'];
                        return [value, name];
                      }} 
                      labelFormatter={(timestamp) => {
                        const date = new Date(timestamp as number);
                        const localDateStr = formatDate(timestamp as number);
                        
                        // Calculate Eastern Time display for better context
                        const etOptions: Intl.DateTimeFormatOptions = {
                          timeZone: 'America/New_York',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true
                        };
                        const etTimeStr = date.toLocaleString('en-US', etOptions);
                        
                        // Get session information to show in tooltip
                        const session = getMarketSessionInfo(timestamp as number);
                        
                        // Find corresponding data point for additional info
                        const dataPoint = chartData.find(d => d.timestamp === timestamp);
                        const prevClose = dataPoint?.prevClose;
                        
                        // Calculate change from previous close if available
                        let changeFromPrevClose = null;
                        let changePercentFromPrevClose = null;
                        
                        if (prevClose && dataPoint) {
                          changeFromPrevClose = dataPoint.price - prevClose;
                          changePercentFromPrevClose = (changeFromPrevClose / prevClose) * 100;
                        }
                        
                        // Enhanced tooltip content - Google Finance style
                        return (
                          <div className="tooltip-content">
                            <div className="font-medium">{localDateStr}</div>
                            <div className="text-xs mt-1">{etTimeStr} ET</div>
                            
                            {/* Show session type for intraday view */}
                            {selectedTimeRange === '1d' && (
                              <div className={`text-xs mt-1 font-medium ${
                                session.sessionType === 'regular' ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                                session.sessionType === 'pre' ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') :
                                session.sessionType === 'after' ? (isDarkMode ? 'text-purple-400' : 'text-purple-600') :
                                (isDarkMode ? 'text-gray-400' : 'text-gray-600')
                              }`}>
                                {session.label}
                              </div>
                            )}
                            
                            {/* Show change from previous close if available */}
                            {prevClose && changeFromPrevClose !== null && (
                              <div className={`text-xs mt-1 ${
                                changeFromPrevClose >= 0 
                                  ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                                  : (isDarkMode ? 'text-red-400' : 'text-red-600')
                              }`}>
                                vs Prev Close: {changeFromPrevClose >= 0 ? '+' : ''}{changeFromPrevClose.toFixed(2)} ({changePercentFromPrevClose?.toFixed(2)}%)
                              </div>
                            )}
                            
                            {/* Show volume if available */}
                            {dataPoint && (
                              <div className="text-xs mt-1">
                                Volume: {dataPoint.volume.toLocaleString()}
                              </div>
                            )}
                          </div>
                        );
                      }}
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.5)' : 'rgba(209, 213, 219, 0.8)',
                        color: isDarkMode ? 'white' : 'black',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                        padding: '12px'
                      }}
                    />
                    
                    {/* Improved X/Y axes with better formatting */}
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={formatDate} 
                      stroke={isDarkMode ? 'rgba(156, 163, 175, 0.5)' : 'rgba(107, 114, 128, 0.6)'} 
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: isDarkMode ? 'rgba(55, 65, 81, 0.4)' : 'rgba(209, 213, 219, 0.7)' }}
                      minTickGap={40}
                      padding={{ left: 10, right: 10 }}
                    />
                    <YAxis 
                      domain={['auto', 'auto']}
                      stroke={isDarkMode ? 'rgba(156, 163, 175, 0.5)' : 'rgba(107, 114, 128, 0.6)'} 
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: isDarkMode ? 'rgba(55, 65, 81, 0.4)' : 'rgba(209, 213, 219, 0.7)' }}
                      orientation="right"
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                      padding={{ top: 10, bottom: 10 }}
                      width={65}
                    />
                    
                    {/* Enhanced price area with better gradient and styling */}
                    <Area 
                      type="monotone" 
                      dataKey="price" 
                      stroke="url(#priceLineGradient)" 
                      fill="url(#colorPrice)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={(props: any) => {
                        // Custom dot that's color-coded based on market session
                        let fillColor = isPositive ? '#10B981' : '#EF4444';
                        let size = 6;
                        
                        // Change dot color based on market session for intraday
                        if (selectedTimeRange === '1d' && props.payload) {
                          const session = getMarketSessionInfo(props.payload.timestamp);
                          if (session.sessionType === 'pre') fillColor = '#3B82F6'; // Blue for pre-market
                          else if (session.sessionType === 'regular') fillColor = isPositive ? '#10B981' : '#EF4444'; // Green/Red for regular
                          else if (session.sessionType === 'after') fillColor = '#8B5CF6'; // Purple for after-hours
                          else fillColor = '#6B7280'; // Gray for closed
                        }
                        
                        // Custom dot design inspired by Google Finance
                        return (
                          <g>
                            <circle 
                              cx={props.cx} 
                              cy={props.cy} 
                              r={size} 
                              fill={fillColor}
                              stroke={isDarkMode ? '#1F2937' : '#FFFFFF'}
                              strokeWidth={1.5}
                            />
                            <circle 
                              cx={props.cx} 
                              cy={props.cy} 
                              r={size + 4} 
                              fill="none"
                              stroke={fillColor}
                              strokeWidth={1}
                              strokeOpacity={0.4}
                            />
                          </g>
                        );
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                
                {/* Enhanced volume histogram with better correlation to price movement */}
                <div className="absolute bottom-0 left-8 right-8 h-[50px] flex items-end">
                  {chartData.map((point, i) => {
                    // Calculate height based on volume relative to max volume
                    const maxVolume = Math.max(...chartData.map(d => d.volume));
                    const height = (point.volume / maxVolume) * 100;
                    
                    // Determine if price went up or down from previous point
                    const isUp = i > 0 ? point.price >= chartData[i-1].price : true;
                    
                    // Get market session for coloring
                    const session = getMarketSessionInfo(point.timestamp);
                    let sessionColor = '';
                    
                    if (selectedTimeRange === '1d') {
                      if (session.sessionType === 'pre') {
                        sessionColor = isUp ? 'bg-blue-500/40' : 'bg-blue-500/20';
                      } else if (session.sessionType === 'regular') {
                        sessionColor = isUp ? 'bg-green-500/40' : 'bg-red-500/40';
                      } else if (session.sessionType === 'after') {
                        sessionColor = isUp ? 'bg-purple-500/40' : 'bg-purple-500/20';
                      } else {
                        sessionColor = isUp ? 'bg-gray-500/30' : 'bg-gray-500/20';
                      }
                    } else {
                      sessionColor = isUp ? 'bg-green-500/30' : 'bg-red-500/30';
                    }
                    
                    // Add significant volume spike indicator
                    const isSignificantVolume = point.volume > (maxVolume * 0.7);
                    
                    return (
                      <div 
                        key={i}
                        className={`flex-1 ${sessionColor} relative`}
                        style={{ 
                          height: `${Math.max(4, height)}%`,
                          marginLeft: i === 0 ? 0 : '1px',
                          transition: 'height 0.2s ease-in-out'
                        }}
                      >
                        {/* Add indicator for significant volume spikes */}
                        {isSignificantVolume && (
                          <div className="absolute -top-1 left-0 right-0 h-1 bg-yellow-400/70"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Session indicator legend - Enhanced visibility */}
                {selectedTimeRange === '1d' && chartData.length > 0 && (
                  <div className="absolute bottom-[60px] left-8 right-8 flex h-1.5 rounded-full overflow-hidden">
                    <div className="bg-blue-500/50 h-full" style={{ width: '23%' }}></div>
                    <div className="bg-green-500/50 h-full" style={{ width: '43%' }}></div>
                    <div className="bg-purple-500/50 h-full" style={{ width: '34%' }}></div>
                  </div>
                )}
                
                {/* OHLC markers for intraday chart */}
                    {selectedTimeRange === '1d' && chartData.length > 0 && (
                      <>
                        {/* Open price marker */}
                        <div 
                          className={`absolute left-8 h-px w-3 border-l-2 ${isDarkMode ? 'border-gray-400' : 'border-gray-500'}`}
                          style={{ 
                            top: `${100 - ((chartData[0].open - Math.min(...chartData.map(d => d.low))) / 
                              (Math.max(...chartData.map(d => d.high)) - Math.min(...chartData.map(d => d.low)))) * 100}%`,
                            transform: 'translateY(-50%)'
                          }}
                        >
                          <div className={`absolute -left-16 -top-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Open
                          </div>
                        </div>
                        
                        {/* High and Low markers */}
                        <div 
                          className={`absolute right-8 h-px w-3 border-r-2 ${isDarkMode ? 'border-green-400' : 'border-green-600'}`}
                          style={{ top: '0%', transform: 'translateY(10px)' }}
                        >
                          <div className={`absolute -right-16 -top-3 text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            High
                          </div>
                        </div>
                        
                        <div 
                          className={`absolute right-8 h-px w-3 border-r-2 ${isDarkMode ? 'border-red-400' : 'border-red-600'}`}
                          style={{ bottom: '0%', transform: 'translateY(-60px)' }}
                        >
                          <div className={`absolute -right-16 -top-3 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                            Low
                          </div>
                        </div>
                      </>
                    )}
              </div>
                
              
              {/* Key Stats - Card design */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 ${isDarkMode ? 'bg-gray-800/20' : 'bg-gray-50/50'} backdrop-blur-sm`}>
                {stockDetails?.marketCap && (
                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-gray-800/50 border-gray-700/40' : 'bg-white/50 border-gray-200/40'
                  } backdrop-blur-sm`}>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} flex items-center`}>
                        <DollarSign className="w-4 h-4 mr-2" /> Market Cap
                      </div>
                      <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100/80'}`}>
                        <DollarSign className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      </div>
                    </div>
                    <div className={`mt-2 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} `}>
                      {formatLargeNumber(stockDetails.marketCap)}
                    </div>
                  </div>
                )}
                
                {stockData.length > 0 && (
                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-gray-800/50 border-gray-700/40' : 'bg-white/50 border-gray-200/40'
                  } backdrop-blur-sm`}>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} flex items-center`}>
                        <BarChart2 className="w-4 h-4 mr-2" /> Volume
                      </div>
                      <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100/80'}`}>
                        <BarChart2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      </div>
                    </div>
                    <div className={`mt-2 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} `}>
                      {stockData[stockData.length - 1].v.toLocaleString()}
                    </div>
                  </div>
                )}
                
                {(stockDetails?.sector || stockDetails?.industry) && (
                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-gray-800/50 border-gray-700/40' : 'bg-white/50 border-gray-200/40'
                  } backdrop-blur-sm sm:col-span-2`}>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} flex items-center`}>
                        <Building2 className="w-4 h-4 mr-2" /> Classification
                      </div>
                      <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100/80'}`}>
                        <Building2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      {stockDetails?.sector && (
                        <div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Sector
                          </div>
                          <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {stockDetails.sector}
                          </div>
                        </div>
                      )}
                      
                      {stockDetails?.industry && (
                        <div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Industry
                          </div>
                          <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {stockDetails.industry}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {stockDetails && <StatisticsPanel stockDetails={stockDetails} isDarkMode={isDarkMode} />}
              
              {stockDetails?.description && (
                        <div className={`p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                          <button
                            onClick={() => setShowMoreInfo(!showMoreInfo)}
                            className={`flex items-center justify-between w-full ${
                              isDarkMode ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'
                            } transition-colors duration-200`}
                          >
                            <div className="flex items-center">
                              <div className={`p-2 rounded-lg mr-3 ${
                                isDarkMode ? 'bg-gray-800/80 text-blue-400' : 'bg-blue-50 text-blue-500'
                              }`}>
                                <Building2 className="w-4 h-4" />
                              </div>
                              <h3 className="font-medium">About {stockDetails.name}</h3>
                            </div>
                            <div className={`transform transition-transform duration-200 ${showMoreInfo ? 'rotate-180' : ''}`}>
                              <ChevronDown className="w-5 h-5" />
                            </div>
                          </button>
                          
                          <AnimatePresence>
                            {showMoreInfo && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className={`mt-4 p-4 rounded-xl border ${
                                  isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white/80 border-gray-200'
                                } backdrop-blur-sm`}>
                                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                                    {stockDetails.description}
                                  </p>
                                  
                                  {stockDetails.homepage && (
                                    <a 
                                      href={stockDetails.homepage}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`mt-4 inline-flex items-center text-sm ${
                                        isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
                                      }`}
                                    >
                                      <Globe className="w-4 h-4 mr-2" />
                                      Visit Website
                                      <ExternalLink className="w-3 h-3 ml-1" />
                                    </a>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                      
                      {/* News Section - Modern cards with hover effects */}
                      <div className={`p-6 ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'} backdrop-blur-sm`}>
  <button
    onClick={() => setShowNews(!showNews)}
    className={`flex items-center justify-between w-full ${
      isDarkMode ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'
    } transition-colors duration-200`}
  >
    <div className="flex items-center">
      <div className={`p-2 rounded-lg mr-3 ${
        isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-50 text-purple-500'
      }`}>
        <MessageSquare className="w-4 h-4" />
      </div>
      <h3 className="font-medium">Recent News</h3>
    </div>
    <div className={`transform transition-transform duration-200 ${showNews ? 'rotate-180' : ''}`}>
      <ChevronDown className="w-5 h-5" />
    </div>
  </button>
  
  <AnimatePresence>
    {showNews && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="mt-4 space-y-3">
          {news.length > 0 ? (
            news.map((item, index) => (
              <motion.a
                key={index}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block p-4 rounded-xl border ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-700/50' 
                    : 'bg-white/80 border-gray-200 hover:bg-blue-50/50'
                } backdrop-blur-sm transition-colors`}
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex justify-between items-start">
                  <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.title}
                  </h4>
                  <ExternalLink className={`w-4 h-4 flex-shrink-0 ml-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </div>
                {item.description && (
                  <p className={`text-sm mb-2 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {item.description}
                  </p>
                )}
                <div className="flex items-center space-x-3 text-xs">
                  <div className="flex items-center">
                    {item.favicon ? (
                      <img 
                        src={item.favicon} 
                        alt={item.source} 
                        className="w-4 h-4 mr-1 rounded-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }} 
                      />
                    ) : null}
                    <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>
                      {item.source}
                    </span>
                  </div>
                  <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                    {item.date}
                  </span>
                </div>
              </motion.a>
            ))
          ) : (
            <div className={`text-center p-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {newsLoading ? (
                <div className="flex flex-col items-center">
                  <LoadingSpinner size="sm" />
                  <p className="mt-2">Loading news...</p>
                </div>
              ) : (
                <p>No recent news found for {ticker}</p>
              )}
            </div>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
</div>
                      
                      {/* Action Buttons */}
                      <div className={`p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} border-t ${
                        isDarkMode ? 'border-gray-800' : 'border-gray-200'
                      }`}>
                        <div className="flex flex-col sm:flex-row gap-3">
                          {/* Analyze with Prism button */}
                          {onAnalyze && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleAnalyzeClick}
                              className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/20"
                            >
                              <div className="mr-2 p-1 rounded-md bg-white/20">
                                <TrendingUp className="w-4 h-4" />
                              </div>
                              Analyze with Prism
                            </motion.button>
                          )}
                          
                          {/* View Full Chart button */}
                          <motion.a
                            href={`https://finance.yahoo.com/quote/${ticker}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-xl border ${
                              isDarkMode 
                                ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-750' 
                                : 'bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100'
                            } transition-colors`}
                          >
                            <div className={`mr-2 p-1 rounded-md ${
                              isDarkMode ? 'bg-gray-700' : 'bg-gray-200/80'
                            }`}>
                              <BarChart2 className={`w-4 h-4 ${
                                isDarkMode ? 'text-blue-400' : 'text-blue-500'
                              }`} />
                            </div>
                            View Full Chart
                          </motion.a>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          );
        };

// Enhanced statistics panel with key financial metrics
const StatisticsPanel: React.FC<{ stockDetails: CompanyDetails; isDarkMode: boolean }> = ({ stockDetails, isDarkMode }) => {
  if (!stockDetails) return null;
  
  // Format large numbers with proper suffixes
  const formatLargeNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Format percentages with proper handling of null/undefined values
  const formatPercent = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return 'N/A';
    return `${(num * 100).toFixed(2)}%`;
  };

  // Check if a value should use positive styling (used for EPS and dividend metrics)
  const isPositiveValue = (value: number | null | undefined): boolean => {
    return value !== null && value !== undefined && value > 0;
  };
  
  // Format date with better error handling
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
      {/* Valuation Metrics */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/60' : 'bg-gray-50/80'} backdrop-blur-sm`}>
        <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>Valuation</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Market Cap</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatLargeNumber(stockDetails.marketCap)}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>P/E Ratio</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.peRatio ? stockDetails.peRatio.toFixed(2) : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>EPS</span>
            <span className={`text-sm font-medium ${
              stockDetails.eps !== undefined && stockDetails.eps !== null
                ? isPositiveValue(stockDetails.eps)
                  ? isDarkMode ? 'text-green-400' : 'text-green-600'
                  : isDarkMode ? 'text-red-400' : 'text-red-600'
                : isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {stockDetails.eps !== null && stockDetails.eps !== undefined ? `$${stockDetails.eps.toFixed(2)}` : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Forward P/E</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.forwardPE ? stockDetails.forwardPE.toFixed(2) : 'N/A'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Dividend Information */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/60' : 'bg-gray-50/80'} backdrop-blur-sm`}>
        <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>Dividends</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Yield</span>
            <span className={`text-sm font-medium ${
              isPositiveValue(stockDetails.dividendYield) 
              ? isDarkMode ? 'text-green-400' : 'text-green-600' 
              : isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {stockDetails.dividendYield !== undefined && stockDetails.dividendYield !== null 
                ? formatPercent(stockDetails.dividendYield) 
                : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Annual Dividend</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.dividendRate !== undefined && stockDetails.dividendRate !== null 
                ? `$${stockDetails.dividendRate.toFixed(2)}` 
                : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ex-Dividend Date</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatDate(stockDetails.exDividendDate)}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payout Ratio</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.payoutRatio !== undefined && stockDetails.payoutRatio !== null 
                ? formatPercent(stockDetails.payoutRatio) 
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Price Statistics */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/60' : 'bg-gray-50/80'} backdrop-blur-sm`}>
        <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>Price Range</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>52-Week High</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              {stockDetails.fiftyTwoWeekHigh !== undefined && stockDetails.fiftyTwoWeekHigh !== null 
                ? `$${stockDetails.fiftyTwoWeekHigh.toFixed(2)}` 
                : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>52-Week Low</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              {stockDetails.fiftyTwoWeekLow !== undefined && stockDetails.fiftyTwoWeekLow !== null 
                ? `$${stockDetails.fiftyTwoWeekLow.toFixed(2)}` 
                : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>50-Day Avg</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.fiftyDayAverage !== undefined && stockDetails.fiftyDayAverage !== null 
                ? `$${stockDetails.fiftyDayAverage.toFixed(2)}` 
                : 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>200-Day Avg</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {stockDetails.twoHundredDayAverage !== undefined && stockDetails.twoHundredDayAverage !== null 
                ? `$${stockDetails.twoHundredDayAverage.toFixed(2)}` 
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Implementing a smarter data display algorithm that fills the chart more naturally as the day progresses
const optimizeChartDisplay = (data: StockPrice[], timeRange: TimeRange): StockPrice[] => {
  // If not dealing with intraday data, just return the original data
  if (timeRange !== '1d' && timeRange !== '5d') return data;
  
  // For 1-day view, implement special handling for market hours
  if (timeRange === '1d') {
    const now = new Date();
    
    // Convert timezone to ET to check market hours
    const etOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    };
    const etTime = now.toLocaleString('en-US', etOptions);
    const [etHours, etMinutes] = etTime.split(':').map(Number);
    const etTimeInMinutes = etHours * 60 + etMinutes;
    
    // Market open is 9:30 AM ET = 9.5*60 = 570 minutes
    // Market close is 4:00 PM ET = 16*60 = 960 minutes
    const marketOpenMinutes = 9.5 * 60;
    const marketCloseMinutes = 16 * 60;
    
    // Check if we're in regular trading hours (9:30 AM to 4:00 PM ET)
    if (etTimeInMinutes >= marketOpenMinutes && etTimeInMinutes < marketCloseMinutes) {
      // Calculate how far we are into the trading day (0-1)
      const tradingDayProgress = Math.min(
        (etTimeInMinutes - marketOpenMinutes) / (marketCloseMinutes - marketOpenMinutes),
        1
      );
      
      // Ensure we show at least the first 25% of the chart even at market open
      const visiblePortionOfDay = Math.max(0.25, tradingDayProgress);
      
      // Calculate the number of data points to include
      const pointsToInclude = Math.ceil(data.length * visiblePortionOfDay);
      
      // Return only the portion that should be visible based on time of day
      return data.slice(0, pointsToInclude);
    }
  }
  
  // For 5-day view, ensure we properly filter only market hours data
  if (timeRange === '5d') {
    // Filter out weekend data points and after-hours if not needed
    const filteredData = data.filter(point => {
      const pointDate = new Date(point.t);
      const day = pointDate.getDay();
      
      // Filter out weekends
      if (day === 0 || day === 6) return false;
      
      // Convert point time to ET
      const etOptions: Intl.DateTimeFormatOptions = {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      };
      
      const etTimeStr = pointDate.toLocaleString('en-US', etOptions);
      const [hours, minutes] = etTimeStr.split(':').map(Number);
      const etHours = hours + (minutes / 60);
      
      // Only include points that have valid sessions
      // Early morning (before 4 AM ET) and late night (after 8 PM ET) should be excluded
      return etHours >= 4 && etHours <= 20;
    });
    
    return filteredData;
  }
  
  return data;
};

// Function to ensure consistent data granularity across timeframes
const ensureDataGranularity = (data: StockPrice[], timeRange: TimeRange): StockPrice[] => {
  if (!data || data.length < 2) return data;
  
  // Different minimum data point requirements based on timeframe
  const minimumDataPoints: Record<TimeRange, number> = {
    '1d': 78,    // ~1 point every 5min for 6.5 hour trading day
    '5d': 120,   // ~24 points per day for 5 days
    '1mo': 30,   // ~1 point per day for a month
    '3mo': 60,   // ~20 points per month for 3 months
    '6mo': 130,  // Bi-weekly points for 6 months
    '1y': 250,   // ~1 point per trading day in a year
    '2y': 300,   // ~150 points per year for 2 years
    '5y': 350,   // ~6 points per month for 5 years
    'max': 400   // Reasonable number for full history
  };
  
  const requiredPoints = minimumDataPoints[timeRange] || 100;
  
  // If we already have enough data points, return the original data
  if (data.length >= requiredPoints) {
    // Make sure the latest price is available in all data points for consistent display
    const latestPrice = data[data.length - 1].c;
    return data.map(point => ({
      ...point,
      latestPrice
    }));
  }
  
  // For sparse data, we'll interpolate additional points
  const result: StockPrice[] = [];
  const startTimestamp = data[0].t;
  const endTimestamp = data[data.length - 1].t;
  const timeInterval = (endTimestamp - startTimestamp) / (requiredPoints - 1);
  
  // Collect the latest price for consistent display
  const latestPrice = data[data.length - 1].c;
  
  // Build reference map for existing data points
  const dataMap = new Map<number, StockPrice>();
  data.forEach(point => {
    dataMap.set(point.t, {...point, latestPrice});
  });
  
  // Create interpolated data points
  for (let i = 0; i < requiredPoints; i++) {
    const timestamp = Math.round(startTimestamp + i * timeInterval);
    
    // If we have existing data for this timestamp, use it
    if (dataMap.has(timestamp)) {
      result.push(dataMap.get(timestamp)!);
      continue;
    }
    
    // Find closest points for interpolation
    let lowerPoint: StockPrice | null = null;
    let higherPoint: StockPrice | null = null;
    
    for (const point of data) {
      if (point.t <= timestamp && (!lowerPoint || point.t > lowerPoint.t)) {
        lowerPoint = point;
      }
      if (point.t >= timestamp && (!higherPoint || point.t < higherPoint.t)) {
        higherPoint = point;
      }
    }
    
    // Calculate interpolated values
    if (lowerPoint && higherPoint) {
      const range = higherPoint.t - lowerPoint.t;
      const weight = (timestamp - lowerPoint.t) / range;
      
      // Linear interpolation
      const interpolate = (a: number, b: number) => a + (b - a) * weight;
      
      const interpolatedPoint: StockPrice = {
        t: timestamp,
        o: interpolate(lowerPoint.o, higherPoint.o),
        h: interpolate(lowerPoint.h, higherPoint.h),
        l: interpolate(lowerPoint.l, higherPoint.l),
        c: interpolate(lowerPoint.c, higherPoint.c),
        v: Math.round(interpolate(lowerPoint.v, higherPoint.v)),
        prevClose: lowerPoint.prevClose || higherPoint.prevClose,
        latestPrice // Include the latest price for consistent display
      };
      
      result.push(interpolatedPoint);
    } else if (lowerPoint) {
      // If we only have points before the timestamp, use the last known value
      result.push({...lowerPoint, t: timestamp, latestPrice});
    } else if (higherPoint) {
      // If we only have points after the timestamp, use the first known value
      result.push({...higherPoint, t: timestamp, latestPrice});
    }
  }
  
  // Sort by timestamp to ensure correct order
  return result.sort((a, b) => a.t - b.t);
};
